import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        navigate('/dashboard');
      } else if (result.needsVerification) {
        setNeedsVerification(true);
        setUnverifiedEmail(result.email || data.email);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    await resendVerification(unverifiedEmail);
  };

  // Show verification needed message
  if (needsVerification) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Logo className="h-10" showText={true} variant="dark" />
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border border-neutral-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <EnvelopeIcon className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-neutral-800">Email Verification Required</h2>
            <p className="mt-2 text-neutral-500">
              Please verify your email address before signing in. Check your inbox for <span className="font-medium text-neutral-700">{unverifiedEmail}</span>.
            </p>
            <div className="mt-6 space-y-3">
              <button onClick={handleResendVerification} className="btn-primary w-full">
                Resend verification email
              </button>
              <button onClick={() => setNeedsVerification(false)} className="btn-secondary w-full">
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Left Panel - Brand Message (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-30" />
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-primary-600/10 rounded-full blur-3xl" />
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="#FF7900" opacity="0.5" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="mb-12"><Logo className="h-10" showText={true} variant="light" /></div>
          <div className="space-y-6 max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Connect with your<span className="block text-primary-500">[ customers ]</span>like never before
            </h1>
            <p className="text-lg text-neutral-400 leading-relaxed">Manage client relationships, automate conversations, and scale your communication with our powerful platform.</p>
            <div className="pt-8 space-y-4">
              {['Automated chatbot workflows', 'Real-time analytics & insights', 'Team collaboration & management'].map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-neutral-300">
                  <div className="w-2 h-2 rounded-full bg-primary-500" /><span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 left-12 xl:left-20 flex items-center gap-2 text-neutral-500 text-sm">
            <div className="w-8 h-px bg-primary-500" /><span>Powered by chatza</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex justify-center mb-8"><Logo className="h-10" showText={true} variant="dark" /></div>
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold text-neutral-800">Welcome back</h2>
            <p className="mt-2 text-neutral-500">Sign in to access your client management dashboard</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-neutral-200">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="input-label">Email Address</label>
                <input
                  {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' } })}
                  type="email"
                  className={`input-field ${errors.email ? 'input-error' : ''}`}
                  placeholder="name@company.com"
                />
                {errors.email && <p className="input-error-message">{errors.email.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="input-label">Password</label>
                <div className="relative">
                  <input
                    {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
                    type={showPassword ? 'text' : 'password'}
                    className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="input-error-message">{errors.password.message}</p>}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500 focus:ring-offset-0" />
                  <span className="ml-2 text-sm text-neutral-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">Forgot password?</Link>
              </div>

              <button type="submit" disabled={isLoading} className="btn-primary w-full">
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="spinner spinner-sm border-white/30 border-t-white" /><span>Signing in...</span>
                  </div>
                ) : 'Sign in'}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-neutral-400">New to Chatza?</span></div>
            </div>
            <Link to="/register" className="btn-outline w-full">Create an account</Link>
          </div>
          <p className="text-center text-xs text-neutral-400">&copy; 2025 Chatza. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
